import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  Plus,
  Edit,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Filter,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import inquiryEscalationMatrixMasterService from '../../api/inquiryEscalationMatrixMasterService';
import inquiryCategoryMasterService from '../../api/inquiryCategoryMasterService';
import inquirySubCategoryMasterService from '../../api/inquirySubCategoryMasterService';
import inquiryPriorityLevelMasterService from '../../api/inquiryPriorityLevelMasterService';
import inquiryEscalationLevelMasterService from '../../api/inquiryEscalationLevelMasterService';
import { getRoles } from '../../api/registrationService';
import { getRoleCodeFromId } from '../../utils/roleMapper';

const PAGE_SIZE = 10;
const ALLOWED_ROLES = new Set(['ADMIN', 'SUPER_ADMIN']);

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const emptyLevelConfig = () => ({
  matrixRowId: null,
  escalationLevelId: '',
  responsibleRoleId: '',
  escalationAfterHours: '',
  escalationAfterMinutes: ''
});

const buildDefaultLevelConfigs = (levels) => {
  const active = (Array.isArray(levels) ? levels : []).filter((l) => l.status !== false);
  if (!active.length) return [emptyLevelConfig()];
  return active.map((level) => ({
    matrixRowId: null,
    escalationLevelId: String(level.id),
    responsibleRoleId: '',
    escalationAfterHours: '',
    escalationAfterMinutes: ''
  }));
};

const emptyForm = () => ({
  inquiryCategoryId: '',
  inquirySubCategoryId: '',
  priorityLevelId: '',
  levelConfigs: [emptyLevelConfig()],
  description: '',
  status: true
});

const sortLevelConfigsBySequence = (configs, escalationLevels) => {
  const seqById = new Map(
    (Array.isArray(escalationLevels) ? escalationLevels : []).map((l) => [String(l.id), l.sequenceNumber ?? 0])
  );
  return configs
    .map((config, index) => ({ ...config, _index: index }))
    .sort((a, b) => {
      const seqA = seqById.get(a.escalationLevelId) ?? 9999;
      const seqB = seqById.get(b.escalationLevelId) ?? 9999;
      return seqA - seqB || a._index - b._index;
    });
};

const validateLevelDuration = (hoursRaw, minutesRaw) => {
  if (hoursRaw === '' || hoursRaw == null) return 'Hours is required for each escalation level';
  if (minutesRaw === '' || minutesRaw == null) return 'Minutes is required for each escalation level';
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isInteger(hours) || hours < 0) return 'Hours must be a whole number greater than or equal to 0';
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
    return 'Minutes must be a whole number between 0 and 59';
  }
  if (hours === 0 && minutes === 0) return 'Hours and minutes cannot both be zero';
  return null;
};

const formatDuration = (hours, minutes) => {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

/**
 * Inquiry Escalation Matrix Master — ADMIN / SUPER_ADMIN only.
 */
const InquiryEscalationMatrixManagement = () => {
  const roleId = Number(sessionStorage.getItem('roleId') || sessionStorage.getItem('role_id') || 0);
  const roleCode = (getRoleCodeFromId(roleId) || sessionStorage.getItem('roleCode') || '').toUpperCase();
  const canAccess = ALLOWED_ROLES.has(roleCode);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [escalationLevels, setEscalationLevels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleNameById, setRoleNameById] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const activeEscalationLevels = useMemo(
    () => escalationLevels.filter((level) => level.status !== false),
    [escalationLevels]
  );

  const sortedLevelConfigs = useMemo(
    () => sortLevelConfigsBySequence(formData.levelConfigs, escalationLevels),
    [formData.levelConfigs, escalationLevels]
  );

  const configuredLevelIds = useMemo(
    () => new Set(formData.levelConfigs.map((c) => c.escalationLevelId).filter(Boolean)),
    [formData.levelConfigs]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter]);

  const loadIndependentDropdowns = useCallback(async () => {
    if (!canAccess) return;
    try {
      const [cats, pris, levels, rolesResp] = await Promise.all([
        inquiryCategoryMasterService.getAll(true),
        inquiryPriorityLevelMasterService.getAll(true),
        inquiryEscalationLevelMasterService.getAll(true),
        getRoles()
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setPriorities(Array.isArray(pris) ? pris : []);
      const levelList = Array.isArray(levels) ? levels : [];
      setEscalationLevels(
        [...levelList].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
      );
      const roleList = Array.isArray(rolesResp?.data) ? rolesResp.data : Array.isArray(rolesResp) ? rolesResp : [];
      setRoles(roleList);
      const map = {};
      roleList.forEach((r) => {
        if (r.roleId != null) map[r.roleId] = r.label || r.roleCode || String(r.roleId);
      });
      setRoleNameById(map);
    } catch (error) {
      console.error('Error loading dropdown masters:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load dropdown options');
    }
  }, [canAccess]);

  useEffect(() => {
    loadIndependentDropdowns();
  }, [loadIndependentDropdowns]);

  const loadSubCategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return;
    }
    try {
      const data = await inquirySubCategoryMasterService.getAll(true, Number(categoryId));
      setSubCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading inquiry sub categories:', error);
      setSubCategories([]);
    }
  }, []);

  const loadPage = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const statusParam =
        statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
      const pageData = await inquiryEscalationMatrixMasterService.search({
        search: debouncedSearch,
        status: statusParam,
        page: currentPage,
        size: PAGE_SIZE
      });
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      setRows(content);
      setTotalPages(pageData?.totalPages ?? 0);
      setTotalElements(pageData?.totalElements ?? content.length);
    } catch (error) {
      console.error('Error loading inquiry escalation matrix:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load inquiry escalation matrix');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [canAccess, debouncedSearch, statusFilter, currentPage]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  const openCreate = () => {
    setEditingGroup(null);
    setFormData({
      ...emptyForm(),
      levelConfigs: buildDefaultLevelConfigs(activeEscalationLevels)
    });
    setSubCategories([]);
    setShowModal(true);
    loadIndependentDropdowns();
  };

  const openEdit = async (row) => {
    setShowModal(true);
    await loadIndependentDropdowns();

    let siblings = rows.filter(
      (r) =>
        r.inquiryCategoryId === row.inquiryCategoryId &&
        r.inquirySubCategoryId === row.inquirySubCategoryId &&
        r.priorityLevelId === row.priorityLevelId
    );

    if (!siblings.some((r) => r.id === row.id)) {
      siblings = [...siblings, row];
    }

    let allGroupRows = siblings;

    try {
      const allRows = await inquiryEscalationMatrixMasterService.getAll();
      const fromAll = (Array.isArray(allRows) ? allRows : []).filter(
        (r) =>
          r.inquiryCategoryId === row.inquiryCategoryId &&
          r.inquirySubCategoryId === row.inquirySubCategoryId &&
          r.priorityLevelId === row.priorityLevelId
      );
      if (fromAll.length) {
        allGroupRows = fromAll;
      }
      if (fromAll.length > siblings.length) {
        siblings = fromAll.filter((r) => r.status !== false);
      }
    } catch (error) {
      console.error('Error loading sibling escalation matrix rows:', error);
    }

    const sortedSiblings = sortLevelConfigsBySequence(
      siblings.map((s) => ({
        matrixRowId: s.id,
        escalationLevelId: s.escalationLevelId != null ? String(s.escalationLevelId) : '',
        responsibleRoleId: s.responsibleRoleId != null ? String(s.responsibleRoleId) : '',
        escalationAfterHours: s.escalationAfterHours != null ? String(s.escalationAfterHours) : '',
        escalationAfterMinutes: s.escalationAfterMinutes != null ? String(s.escalationAfterMinutes) : ''
      })),
      escalationLevels
    );

    setEditingGroup({
      inquiryCategoryId: row.inquiryCategoryId,
      inquirySubCategoryId: row.inquirySubCategoryId,
      priorityLevelId: row.priorityLevelId,
      originalRowIds: allGroupRows.map((s) => s.id).filter(Boolean),
      allGroupRows
    });
    setFormData({
      inquiryCategoryId: row.inquiryCategoryId != null ? String(row.inquiryCategoryId) : '',
      inquirySubCategoryId: row.inquirySubCategoryId != null ? String(row.inquirySubCategoryId) : '',
      priorityLevelId: row.priorityLevelId != null ? String(row.priorityLevelId) : '',
      levelConfigs: sortedSiblings.length ? sortedSiblings : [emptyLevelConfig()],
      description: row.description || siblings[0]?.description || '',
      status: siblings.every((s) => s.status !== false)
    });
    if (row.inquiryCategoryId != null) {
      await loadSubCategories(row.inquiryCategoryId);
    }
  };

  const handleCategoryChange = (value) => {
    setFormData((f) => ({
      ...f,
      inquiryCategoryId: value,
      inquirySubCategoryId: ''
    }));
    loadSubCategories(value);
  };

  const updateLevelConfig = (index, field, value) => {
    setFormData((f) => ({
      ...f,
      levelConfigs: f.levelConfigs.map((config, i) => (i === index ? { ...config, [field]: value } : config))
    }));
  };

  const addLevelConfig = () => {
    const usedIds = new Set(formData.levelConfigs.map((c) => c.escalationLevelId).filter(Boolean));
    const nextLevel = activeEscalationLevels.find((level) => !usedIds.has(String(level.id)));
    setFormData((f) => ({
      ...f,
      levelConfigs: [
        ...f.levelConfigs,
        {
          matrixRowId: null,
          escalationLevelId: nextLevel ? String(nextLevel.id) : '',
          responsibleRoleId: '',
          escalationAfterHours: '',
          escalationAfterMinutes: ''
        }
      ]
    }));
  };

  const removeLevelConfig = (index) => {
    setFormData((f) => ({
      ...f,
      levelConfigs: f.levelConfigs.filter((_, i) => i !== index)
    }));
  };

  const resolveLevelLabel = (levelId) => {
    const level = escalationLevels.find((l) => String(l.id) === String(levelId));
    if (!level) return 'Escalation level';
    return level.sequenceNumber != null
      ? `${level.escalationLevelName} (Seq ${level.sequenceNumber})`
      : level.escalationLevelName;
  };

  const handleSave = async () => {
    if (!formData.inquiryCategoryId) {
      toast.error('Inquiry category is required');
      return;
    }
    if (!formData.inquirySubCategoryId) {
      toast.error('Inquiry sub category is required');
      return;
    }
    if (!formData.priorityLevelId) {
      toast.error('Priority level is required');
      return;
    }
    if (!formData.levelConfigs.length) {
      toast.error('At least one escalation level is required');
      return;
    }

    const seenLevelIds = new Set();
    for (let i = 0; i < formData.levelConfigs.length; i += 1) {
      const config = formData.levelConfigs[i];
      const levelLabel = resolveLevelLabel(config.escalationLevelId) || `Level ${i + 1}`;

      if (!config.escalationLevelId) {
        toast.error('Escalation level is required for each configured row');
        return;
      }
      if (seenLevelIds.has(config.escalationLevelId)) {
        toast.error('Each escalation level can only be configured once');
        return;
      }
      seenLevelIds.add(config.escalationLevelId);

      if (!config.responsibleRoleId) {
        toast.error(`Responsible role is required for ${levelLabel}`);
        return;
      }

      const durationError = validateLevelDuration(config.escalationAfterHours, config.escalationAfterMinutes);
      if (durationError) {
        toast.error(`${durationError} (${levelLabel})`);
        return;
      }
    }

    const sharedPayload = {
      inquiryCategoryId: Number(formData.inquiryCategoryId),
      inquirySubCategoryId: Number(formData.inquirySubCategoryId),
      priorityLevelId: Number(formData.priorityLevelId),
      description: formData.description?.trim() || '',
      status: formData.status !== false
    };

    setSaving(true);
    try {
      const savedRowIds = [];

      for (const config of sortLevelConfigsBySequence(formData.levelConfigs, escalationLevels)) {
        const payload = {
          ...sharedPayload,
          escalationLevelId: Number(config.escalationLevelId),
          responsibleRoleId: Number(config.responsibleRoleId),
          escalationAfterHours: Number(config.escalationAfterHours),
          escalationAfterMinutes: Number(config.escalationAfterMinutes)
        };

        if (config.matrixRowId) {
          await inquiryEscalationMatrixMasterService.update(config.matrixRowId, payload);
          savedRowIds.push(config.matrixRowId);
          continue;
        }

        const existingInactive = (editingGroup?.allGroupRows || []).find(
          (row) =>
            row.id != null &&
            row.escalationLevelId === payload.escalationLevelId &&
            row.status === false
        );

        if (existingInactive?.id) {
          await inquiryEscalationMatrixMasterService.update(existingInactive.id, payload);
          savedRowIds.push(existingInactive.id);
        } else {
          const created = await inquiryEscalationMatrixMasterService.create(payload);
          if (created?.id != null) savedRowIds.push(created.id);
        }
      }

      if (editingGroup?.originalRowIds?.length) {
        const keptIds = new Set(
          formData.levelConfigs.map((c) => c.matrixRowId).filter(Boolean)
        );
        for (const rowId of editingGroup.originalRowIds) {
          if (!keptIds.has(rowId)) {
            await inquiryEscalationMatrixMasterService.updateStatus(rowId, false);
          }
        }
      }

      toast.success(
        editingGroup
          ? `Escalation matrix updated (${savedRowIds.length || formData.levelConfigs.length} level${formData.levelConfigs.length === 1 ? '' : 's'})`
          : `Escalation matrix created (${formData.levelConfigs.length} level${formData.levelConfigs.length === 1 ? '' : 's'})`
      );
      setShowModal(false);
      setEditingGroup(null);
      await loadPage();
    } catch (error) {
      console.error('Error saving inquiry escalation matrix:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save inquiry escalation matrix');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const next = !(row.status !== false);
    try {
      await inquiryEscalationMatrixMasterService.updateStatus(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      toast.success(`Inquiry escalation matrix ${next ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling inquiry escalation matrix status:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    }
  };

  const resolveRoleName = (id) => {
    if (id == null) return '—';
    return roleNameById[id] || roleNameById[String(id)] || String(id);
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ListChecks className="h-7 w-7 text-primary" />
            Inquiry Escalation Matrix Master
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure escalation paths by category, sub category, priority, and sequential escalation levels.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPage} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Escalation Matrix
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by category, sub category, priority, escalation level, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <label className="text-sm font-medium text-foreground mb-1 block">Status</label>
            <StatusFilterDropdown
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Filter by Status"
              onChange={setStatusFilter}
              icon={Filter}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left p-3 text-sm font-semibold">S.No</th>
                  <th className="text-left p-3 text-sm font-semibold">Category</th>
                  <th className="text-left p-3 text-sm font-semibold">Sub Category</th>
                  <th className="text-left p-3 text-sm font-semibold">Priority</th>
                  <th className="text-left p-3 text-sm font-semibold">Escalation Level</th>
                  <th className="text-left p-3 text-sm font-semibold">Role</th>
                  <th className="text-left p-3 text-sm font-semibold">Escalate After</th>
                  <th className="text-left p-3 text-sm font-semibold">Status</th>
                  <th className="text-left p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted-foreground">
                      No inquiry escalation matrix rows found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 text-sm">{currentPage * PAGE_SIZE + index + 1}</td>
                      <td className="p-3 text-sm">{row.inquiryCategoryName || '—'}</td>
                      <td className="p-3 text-sm">{row.inquirySubCategoryName || '—'}</td>
                      <td className="p-3 text-sm">{row.priorityLevelName || '—'}</td>
                      <td className="p-3 text-sm">{row.escalationLevelName || '—'}</td>
                      <td className="p-3 text-sm">{resolveRoleName(row.responsibleRoleId)}</td>
                      <td className="p-3 text-sm">
                        {formatDuration(row.escalationAfterHours, row.escalationAfterMinutes)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.status !== false}
                            onCheckedChange={() => handleToggleStatus(row)}
                          />
                          <Badge className={row.status !== false
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'}>
                            {row.status !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {totalElements} total · Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 0 || loading}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages - 1 || totalPages === 0 || loading}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editingGroup ? 'Edit Escalation Matrix' : 'Add Escalation Matrix'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Inquiry Category *</label>
            <Select
              value={formData.inquiryCategoryId}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger disabled={Boolean(editingGroup)}>
                <SelectValue placeholder="Select inquiry category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.inquiryCategoryName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Inquiry Sub Category *</label>
            <Select
              value={formData.inquirySubCategoryId}
              onValueChange={(value) => setFormData((f) => ({ ...f, inquirySubCategoryId: value }))}
            >
              <SelectTrigger disabled={Boolean(editingGroup) || !formData.inquiryCategoryId}>
                <SelectValue placeholder={formData.inquiryCategoryId ? 'Select sub category' : 'Select category first'} />
              </SelectTrigger>
              <SelectContent>
                {subCategories.map((sub) => (
                  <SelectItem key={sub.id} value={String(sub.id)}>
                    {sub.inquirySubCategoryName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Priority Level *</label>
            <Select
              value={formData.priorityLevelId}
              onValueChange={(value) => setFormData((f) => ({ ...f, priorityLevelId: value }))}
            >
              <SelectTrigger disabled={Boolean(editingGroup)}>
                <SelectValue placeholder="Select priority level" />
              </SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.priorityLevelName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <label className="text-sm font-medium block">Escalation Levels *</label>
                <p className="text-xs text-muted-foreground mt-1">
                  Configure role and escalate-after duration for each escalation stage.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLevelConfig}
                disabled={configuredLevelIds.size >= activeEscalationLevels.length}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Level
              </Button>
            </div>

            {sortedLevelConfigs.map((config) => (
              <div
                key={`${config._index}-${config.matrixRowId || 'new'}-${config.escalationLevelId || 'empty'}`}
                className="rounded-lg border border-border p-3 space-y-3 bg-muted/20"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">
                    {config.escalationLevelId ? resolveLevelLabel(config.escalationLevelId) : 'New escalation level'}
                  </p>
                  {formData.levelConfigs.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-destructive hover:text-destructive"
                      onClick={() => removeLevelConfig(config._index)}
                      aria-label="Remove escalation level"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Escalation Level *</label>
                  <Select
                    value={config.escalationLevelId}
                    onValueChange={(value) => updateLevelConfig(config._index, 'escalationLevelId', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select escalation level" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeEscalationLevels.map((level) => {
                        const levelId = String(level.id);
                        const isUsedElsewhere =
                          configuredLevelIds.has(levelId) && levelId !== config.escalationLevelId;
                        return (
                          <SelectItem key={level.id} value={levelId} disabled={isUsedElsewhere}>
                            {level.escalationLevelName}
                            {level.sequenceNumber != null ? ` (Seq ${level.sequenceNumber})` : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Responsible Role *</label>
                  <Select
                    value={config.responsibleRoleId}
                    onValueChange={(value) => updateLevelConfig(config._index, 'responsibleRoleId', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select responsible role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles
                        .filter((r) => r.roleId != null)
                        .map((r) => (
                          <SelectItem key={r.roleId} value={String(r.roleId)}>
                            {r.label || r.roleCode}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Escalate After</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Hours *</label>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={config.escalationAfterHours}
                        onChange={(e) => updateLevelConfig(config._index, 'escalationAfterHours', e.target.value)}
                        placeholder="e.g. 24"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Minutes *</label>
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        step={1}
                        value={config.escalationAfterMinutes}
                        onChange={(e) => updateLevelConfig(config._index, 'escalationAfterMinutes', e.target.value)}
                        placeholder="e.g. 0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.status !== false}
              onCheckedChange={(checked) => setFormData((f) => ({ ...f, status: checked }))}
            />
            <span className="text-sm">Active</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InquiryEscalationMatrixManagement;
